import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import ePub from "epubjs";
import { api } from "@/lib/api";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  ArrowLeft, ChevronLeft, ChevronRight, Loader2, List, X, Download, Pencil, Check,
} from "lucide-react";

async function downloadFile(url, filename) {
  const { data } = await api.get(url, { responseType: "blob" });
  const href = URL.createObjectURL(data);
  const a = document.createElement("a");
  a.href = href; a.download = filename; document.body.appendChild(a); a.click();
  a.remove(); URL.revokeObjectURL(href);
}

// Flattens epub.js's nested TOC (nav.toc, each item optionally has
// .subitems) into a single list with a depth marker, so the sidebar can
// render an indented, clickable list of every link that appears in the
// book's actual table of contents -- exactly what the reader would see
// clicking through the EPUB's own navigation.
function flattenToc(items, depth = 0, out = []) {
  for (const item of items || []) {
    out.push({ id: item.id, label: item.label?.trim() || "Untitled", href: item.href, depth });
    if (item.subitems?.length) flattenToc(item.subitems, depth + 1, out);
  }
  return out;
}

export default function EpubPreview() {
  const { id, editionId } = useParams();
  const navigate = useNavigate();
  const viewerRef = useRef(null);
  const bookRef = useRef(null);
  const renditionRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [meta, setMeta] = useState(null); // { title, langName }
  const [toc, setToc] = useState([]);
  const [activeHref, setActiveHref] = useState(null);
  const [tocOpen, setTocOpen] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorLoading, setEditorLoading] = useState(false);
  const [editorChapter, setEditorChapter] = useState(null); // { ci, title, passages, customPrefaceActive, prefacePassages }
  const [editorDrafts, setEditorDrafts] = useState({}); // { [pi]: text }
  const [prefaceDrafts, setPrefaceDrafts] = useState({}); // { [index]: text }
  const [savingPi, setSavingPi] = useState(null);
  const [savingPrefaceIdx, setSavingPrefaceIdx] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const pendingHrefRef = useRef(null);

  const currentChapterIndex = (() => {
    const m = activeHref && activeHref.match(/chap_(\d+)\.xhtml/);
    return m ? parseInt(m[1], 10) - 1 : null;
  })();

  const openChapterEditor = useCallback(async (ci) => {
    if (ci == null) { toast.error("Navigheaza mai intai la un capitol"); return; }
    setEditorOpen(true);
    setEditorLoading(true);
    setEditorChapter(null);
    try {
      const { data } = await api.get(`/editions/${editionId}/chapters/${ci}/passages`);
      setEditorChapter({
        ci, title: data.chapter_title, passages: data.passages,
        customPrefaceActive: data.custom_preface_active, prefacePassages: data.preface_passages || null,
      });
      const drafts = {};
      for (const p of data.passages) drafts[p.pi] = p.translated;
      setEditorDrafts(drafts);
      const pDrafts = {};
      for (const p of data.preface_passages || []) pDrafts[p.index] = p.text;
      setPrefaceDrafts(pDrafts);
    } catch (e) { toast.error("Nu s-au putut incarca pasajele acestui capitol"); setEditorOpen(false); }
    finally { setEditorLoading(false); }
  }, [editionId]);

  const savePassage = async (pi) => {
    if (!editorChapter) return;
    const text = (editorDrafts[pi] || "").trim();
    if (!text) { toast.error("Textul nu poate fi gol"); return; }
    setSavingPi(pi);
    try {
      await api.post(`/editions/${editionId}/passages`, { chapter_index: editorChapter.ci, passage_index: pi, text });
      setEditorChapter((c) => c && ({ ...c, passages: c.passages.map((p) => p.pi === pi ? { ...p, translated: text } : p) }));
      toast.success("Pasaj salvat -- preview-ul se actualizeaza");
      pendingHrefRef.current = activeHref;
      setReloadKey((k) => k + 1);
    } catch (e) { toast.error("Salvarea a esuat"); }
    finally { setSavingPi(null); }
  };

  const savePrefacePassage = async (index) => {
    if (!editorChapter) return;
    const text = (prefaceDrafts[index] || "").trim();
    if (!text) { toast.error("Textul nu poate fi gol"); return; }
    setSavingPrefaceIdx(index);
    try {
      await api.post(`/editions/${editionId}/preface-passages`, { passage_index: index, text });
      setEditorChapter((c) => c && ({
        ...c,
        prefacePassages: c.prefacePassages.map((p) => p.index === index ? { ...p, text } : p),
      }));
      toast.success("Text salvat -- preview-ul se actualizeaza");
      pendingHrefRef.current = activeHref;
      setReloadKey((k) => k + 1);
    } catch (e) { toast.error("Salvarea a esuat"); }
    finally { setSavingPrefaceIdx(null); }
  };

  const download = async () => {
    setDownloading(true);
    try {
      await downloadFile(`/editions/${editionId}/epub`, `${meta?.title || "book"}_${editionId}.epub`);
    } catch (e) { toast.error("EPUB not ready"); }
    finally { setDownloading(false); }
  };

  const goTo = useCallback((href) => {
    renditionRef.current?.display(href);
  }, []);

  const next = useCallback(() => renditionRef.current?.next(), []);
  const prev = useCallback(() => renditionRef.current?.prev(), []);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const { data: blob } = await api.get(`/editions/${editionId}/epub`, { responseType: "blob" });
        if (cancelled) return;
        const buf = await blob.arrayBuffer();
        if (cancelled) return;

        const book = ePub(buf);
        bookRef.current = book;
        await book.ready;
        if (cancelled) return;

        const rendition = book.renderTo(viewerRef.current, {
          width: "100%", height: "100%", flow: "paginated", spread: "none",
        });
        renditionRef.current = rendition;
        const resumeHref = pendingHrefRef.current;
        pendingHrefRef.current = null;
        await rendition.display(resumeHref || undefined);
        if (cancelled) return;

        rendition.on("relocated", (loc) => setActiveHref(loc?.start?.href));
        rendition.on("rendered", () => setLoading(false));

        book.loaded.metadata.then((m) => {
          if (!cancelled) setMeta({ title: m.title, langName: m.language });
        });
        book.loaded.navigation.then((nav) => {
          if (!cancelled) setToc(flattenToc(nav.toc));
        });
      } catch (e) {
        if (!cancelled) { setError("Couldn't open this EPUB — it may not be built yet."); setLoading(false); }
      }
    }
    load();

    function onKey(e) {
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    }
    window.addEventListener("keydown", onKey);

    return () => {
      cancelled = true;
      window.removeEventListener("keydown", onKey);
      renditionRef.current?.destroy();
      bookRef.current?.destroy();
    };
  }, [editionId, next, prev, reloadKey]);

  return (
    <div className="flex h-screen flex-col bg-[#f6f5f1]">
      <Header />
      <div className="flex items-center justify-between border-b border-[#e6e4dd] bg-white px-4 py-2.5 sm:px-6">
        <div className="flex min-w-0 items-center gap-3">
          <button onClick={() => navigate(`/project/${id}`)} data-testid="back-to-project-btn"
            className="inline-flex items-center gap-1.5 text-sm text-[#475569] hover:text-[#0f172a]">
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
          <div className="min-w-0 border-l border-[#e6e4dd] pl-3">
            <p className="truncate text-sm font-medium text-[#0f172a]">{meta?.title || "Preview"}</p>
            <p className="text-xs text-[#94a3b8]">EPUB preview</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => setTocOpen((v) => !v)} data-testid="toggle-toc-btn"
            className="h-8 rounded-lg border-[#e6e4dd] text-xs">
            {tocOpen ? <X className="mr-1.5 h-3.5 w-3.5" /> : <List className="mr-1.5 h-3.5 w-3.5" />}
            {tocOpen ? "Hide contents" : "Contents"}
          </Button>
          <Button size="sm" variant="outline" onClick={() => openChapterEditor(currentChapterIndex)}
            data-testid="edit-chapter-btn" className="h-8 rounded-lg border-[#e6e4dd] text-xs">
            <Pencil className="mr-1.5 h-3.5 w-3.5" /> Editeaza capitolul
          </Button>
          <Button size="sm" variant="outline" onClick={download} disabled={downloading} data-testid="preview-download-btn"
            className="h-8 rounded-lg border-[#e6e4dd] text-xs">
            {downloading ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Download className="mr-1.5 h-3.5 w-3.5" />}
            EPUB
          </Button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {editorOpen && (
          <aside className="w-96 shrink-0 overflow-y-auto border-r border-[#e6e4dd] bg-white p-4">
            <div className="mb-3 flex items-center justify-between">
              <p className="text-xs font-medium uppercase tracking-wide text-[#94a3b8]">
                Editare manuala {editorChapter ? `-- ${editorChapter.title}` : ""}
              </p>
              <button onClick={() => setEditorOpen(false)} data-testid="close-chapter-editor-btn"
                className="text-[#94a3b8] hover:text-[#0f172a]"><X className="h-4 w-4" /></button>
            </div>
            {editorLoading ? (
              <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-[#0f172a]" /></div>
            ) : !editorChapter ? null : (
              <div className="space-y-4">
                {editorChapter.customPrefaceActive && (
                  <div className="space-y-3">
                    <p className="rounded-lg bg-amber-50 p-2.5 text-xs text-amber-800">
                      Acest capitol are un bloc de prefata personalizata (pagina de titlu +
                      prefata editurii), care apare INAINTEA pasajelor de mai jos in EPUB.
                      Editeaza-l aici mai jos daca vrei sa schimbi ceva din el.
                    </p>
                    {editorChapter.prefacePassages?.map((p) => (
                      <div key={`pref-${p.index}`} className="rounded-xl border border-amber-200 bg-amber-50/40 p-3">
                        <p className="text-[10px] uppercase tracking-[0.1em] text-amber-700">
                          Prefata personalizata (rand {p.index + 1})
                        </p>
                        <Textarea value={prefaceDrafts[p.index] ?? ""} data-testid={`preface-edit-textarea-${p.index}`}
                          onChange={(e) => setPrefaceDrafts((d) => ({ ...d, [p.index]: e.target.value }))}
                          className="mt-2 min-h-[70px] text-sm leading-relaxed text-[#0f172a]" />
                        <div className="mt-2 flex justify-end">
                          <Button size="sm" onClick={() => savePrefacePassage(p.index)}
                            disabled={savingPrefaceIdx === p.index || prefaceDrafts[p.index] === p.text}
                            data-testid={`save-preface-passage-${p.index}`}
                            className="rounded-full bg-[#059669] text-white hover:bg-[#047857] disabled:opacity-40">
                            {savingPrefaceIdx === p.index ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Check className="mr-1.5 h-3.5 w-3.5" />}
                            Salveaza
                          </Button>
                        </div>
                      </div>
                    ))}
                    <p className="px-1 text-[10px] uppercase tracking-[0.1em] text-[#94a3b8]">
                      Pasajele originale ale capitolului (apar dupa prefata de mai sus)
                    </p>
                  </div>
                )}
                {editorChapter.passages.map((p) => (
                  <div key={p.pi} className="rounded-xl border border-[#e6e4dd] p-3">
                    <p className="text-[10px] uppercase tracking-[0.1em] text-[#94a3b8]">Original (pi {p.pi})</p>
                    <p className="mt-1 text-xs leading-relaxed text-[#94a3b8]">{p.original}</p>
                    <Textarea value={editorDrafts[p.pi] ?? ""} data-testid={`chapter-edit-textarea-${p.pi}`}
                      onChange={(e) => setEditorDrafts((d) => ({ ...d, [p.pi]: e.target.value }))}
                      className="mt-2 min-h-[90px] text-sm leading-relaxed text-[#0f172a]" />
                    <div className="mt-2 flex justify-end">
                      <Button size="sm" onClick={() => savePassage(p.pi)}
                        disabled={savingPi === p.pi || editorDrafts[p.pi] === p.translated}
                        data-testid={`save-chapter-passage-${p.pi}`}
                        className="rounded-full bg-[#059669] text-white hover:bg-[#047857] disabled:opacity-40">
                        {savingPi === p.pi ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Check className="mr-1.5 h-3.5 w-3.5" />}
                        Salveaza
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </aside>
        )}
        {tocOpen && (
          <aside className="w-72 shrink-0 overflow-y-auto border-r border-[#e6e4dd] bg-white p-3">
            <p className="mb-2 px-2 text-xs font-medium uppercase tracking-wide text-[#94a3b8]">Table of contents</p>
            {toc.length === 0 && !loading && <p className="px-2 text-xs text-[#94a3b8]">No navigation found in this EPUB.</p>}
            <nav className="space-y-0.5">
              {toc.map((item, i) => {
                const active = activeHref && item.href && activeHref.split("#")[0].endsWith(item.href.split("#")[0]);
                return (
                  <button key={`${item.id || item.href}-${i}`} data-testid={`toc-item-${i}`}
                    onClick={() => goTo(item.href)}
                    style={{ paddingLeft: `${8 + item.depth * 14}px` }}
                    className={`block w-full truncate rounded-lg py-1.5 pr-2 text-left text-sm transition-colors ${
                      active ? "bg-[#0f172a] text-white" : "text-[#334155] hover:bg-slate-100"
                    }`}>
                    {item.label}
                  </button>
                );
              })}
            </nav>
          </aside>
        )}

        <div className="relative flex flex-1 flex-col overflow-hidden bg-[#eceae3]">
          {loading && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-[#eceae3]">
              <Loader2 className="h-6 w-6 animate-spin text-[#0f172a]" />
            </div>
          )}
          {error && (
            <div className="absolute inset-0 z-10 flex items-center justify-center px-6 text-center text-sm text-rose-600">
              {error}
            </div>
          )}
          <div className="flex flex-1 items-stretch justify-center gap-2 overflow-hidden p-4 sm:p-8">
            <button onClick={prev} data-testid="preview-prev-btn"
              className="hidden shrink-0 self-center rounded-full border border-[#e6e4dd] bg-white p-2 text-[#475569] shadow-sm hover:bg-slate-900 hover:text-white sm:flex">
              <ChevronLeft className="h-5 w-5" />
            </button>
            <div className="min-w-0 flex-1 rounded-xl border border-[#e6e4dd] bg-white shadow-sm">
              <div ref={viewerRef} className="h-full w-full" data-testid="epub-viewer" />
            </div>
            <button onClick={next} data-testid="preview-next-btn"
              className="hidden shrink-0 self-center rounded-full border border-[#e6e4dd] bg-white p-2 text-[#475569] shadow-sm hover:bg-slate-900 hover:text-white sm:flex">
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
